import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Grid } from "@mui/material";
import FormTextField from "../../../components/textField/formTextField";
import SimpleTable from "../../../components/tables/simpleTable";
import ToastNotification from "../../../components/toastNotification";
import { icons } from "../../../constants";
import {
  addProtocolActivity,
  callActionApiAsync,
  callApiAsync,
  deleteProtocolActivity,
  getProtocolActivitySelect,
  updateProtocolActivity
} from "../../../services/apiService";

const validationSchema = yup.object().shape({
  newActivity: yup
    .string()
    .required("Este campo es obligatorio")
    .min(1, "Este campo no puede estar vacío")
});

const columns = [{ field: "name", header: "Actividad" }];

const AddProtocolActivity = ({
  open,
  close,
  getProtocolActivities,
  userId,
  isViewer
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "warn",
    summary: "",
    detail: ""
  });
  const [activityRows, setActivityRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      newActivity: ""
    }
  });

  const loadActivities = useCallback(() => {
    callApiAsync(
      () => getProtocolActivitySelect(),
      (data) => {
        setActivityRows(data?.protocolActivities || []);
      }
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    loadActivities();
    setSelectedRow(null);
    reset({ newActivity: "" });
  }, [loadActivities, open, reset]);

  const handleClose = () => {
    setSelectedRow(null);
    reset({ newActivity: "" });
    close();
  };

  const startEdit = useCallback(
    (row) => {
      setSelectedRow(row);
      reset({ newActivity: row?.name || "" });
    },
    [reset]
  );

  const handleDelete = useCallback(
    async (row) => {
      if (!row?.value || !userId) return;
      if (!window.confirm(`¿Eliminar la actividad "${row.name}"?`)) {
        return;
      }

      await callActionApiAsync(
        () =>
          deleteProtocolActivity({
            id: row.value,
            userId
          }),
        () => {
          if (selectedRow?.value === row.value) {
            setSelectedRow(null);
            reset({ newActivity: "" });
          }
          loadActivities();
          getProtocolActivities?.();
        },
        (error) => {
          setToastConfig({
            severity: "error",
            summary: "Actividad de protocolo",
            detail:
              typeof error === "string" && error
                ? error
                : "No se pudo eliminar la actividad."
          });
          setShowToast(true);
        }
      );
    },
    [getProtocolActivities, loadActivities, reset, selectedRow?.value, userId]
  );

  const tableButtons = useMemo(
    () =>
      isViewer
        ? []
        : [
            {
              type: "icon",
              icon: icons.editYellow,
              onClick: startEdit
            },
            {
              type: "icon",
              icon: icons.trash,
              onClick: handleDelete
            }
          ],
    [handleDelete, isViewer, startEdit]
  );

  const onSubmit = async (formData) => {
    const name = String(formData.newActivity || "").trim();

    await callActionApiAsync(
      () =>
        selectedRow
          ? updateProtocolActivity({
              id: selectedRow.value,
              name,
              userId
            })
          : addProtocolActivity({
              newActivity: name,
              userId
            }),
      () => {
        getProtocolActivities?.();
        loadActivities();
        setSelectedRow(null);
        reset({ newActivity: "" });
      },
      (error) => {
        setToastConfig({
          severity: "error",
          summary: "Actividad de protocolo",
          detail:
            typeof error === "string" && error
              ? error
              : "No se pudo guardar la actividad."
        });
        setShowToast(true);
      }
    );
  };

  return (
    <div>
      <ModalComponent
        open={open}
        title="Administrar actividades"
        handleClose={handleClose}
        maxWidth="820px"
      >
        <ToastNotification
          severity={toastConfig.severity}
          summary={toastConfig.summary}
          detail={toastConfig.detail}
          showToast={showToast}
          setShowToast={setShowToast}
        />
        <div className="HContainer">
          <form className="VContainer align-center m-20 w-complete" onSubmit={handleSubmit(onSubmit)}>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12}>
                <p className="create-label">
                  {selectedRow ? "Editar actividad:" : "Nueva actividad:"}
                </p>
                <FormTextField
                  name="newActivity"
                  control={control}
                  error={errors.newActivity}
                  disabled={isViewer}
                />
              </Grid>
              {!isViewer && (
                <Grid item xs={12} className="HContainer j-center">
                  <LaddaButton
                    width={"180px"}
                    fontSize={"12px"}
                    height={"32px"}
                    variant="contained"
                    margin={"0 10px 0 0"}
                    type="submit"
                  >
                    <p className="button_text">
                      {selectedRow ? "Guardar cambios" : "Crear actividad"}
                    </p>
                  </LaddaButton>
                  {selectedRow && (
                    <LaddaButton
                      width={"160px"}
                      fontSize={"12px"}
                      height={"32px"}
                      variant="contained"
                      color={"#404040"}
                      backgroundColor={"#F4D658"}
                      margin={"0"}
                      type="button"
                      onClick={() => {
                        setSelectedRow(null);
                        reset({ newActivity: "" });
                      }}
                    >
                      <p className="button_text">Cancelar edición</p>
                    </LaddaButton>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <div className="mt-15">
                  <SimpleTable
                    data={activityRows}
                    columns={columns}
                    buttons={tableButtons}
                    text={"Actividades registradas"}
                    emptyMessage="No hay actividades registradas."
                    scrollHeight={"260px"}
                  />
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
      </ModalComponent>
    </div>
  );
};

export default AddProtocolActivity;
